import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';
import type { AcaoTipo } from '@/features/acao/types/AcaoTipo';
import type { Cidade } from '@/features/cidade-uf-pais/types';
import type { Cliente } from '@/features/cliente/types/Cliente';
import type { Contrato } from '@/features/contrato/types/Contrato';
import type { FinanceiroCategoria } from '@/features/financeiro-categoria/types/FinanceiroCategoria';
import type { FormaPagamento } from '@/features/forma-pagamento/types/FormaPagamento';
import type { Marcador } from '@/features/marcador/types/Marcador';
import type { Produto } from '@/features/produto/types/Produto';
import type { Romaneio } from '@/features/romaneio/types/Romaneio';
import type { SeparacaoStatus } from '@/features/separacao/types/SeparacaoStatus';
import type { ApiDispositivoSistemasEmUso } from '@/features/sistema-integrado/api';
import type { Transportador } from '@/features/transportador/types/Transportador';
import type { UsuarioEmpresa } from '@/features/usuario/types/UsuarioEmpresa';
import type { Venda } from '@/features/venda/types/Venda';
import type { VendaTipo } from '@/features/venda/types/VendaTipo';
import { VendaDataTipo } from '@/features/venda/types/VendaDataTipo';
import { DATE_FORMAT } from '@/std/const/date';
import { createPaginationStore } from '@/std/pagination/createPaginationStore';

export type VendaFilterTipo = VendaTipo | 'cancelado' | 'total';

export type VendaFilter = {
    tipo: VendaFilterTipo;
    pesquisar: string;
    data_tipo: VendaDataTipo;
    data_inicio: string;
    data_fim: string;
    cliente: Cliente;
    cidade: Cidade;
    transportador: Transportador;
    vendedor: UsuarioEmpresa;
    marcadores: Marcador[];
    contrato: Contrato;
    produtos: Produto[];
    acao: AcaoTipo[];
    categoriasFinanceiras: FinanceiroCategoria[];
    formasPagamento: FormaPagamento[];
    sistemasIntegrados: ApiDispositivoSistemasEmUso[];
    romaneio: Romaneio[];
    separacao_status: SeparacaoStatus[];
};

export const useVendaPaginationStore = createPaginationStore<Venda, VendaFilter>({
    apiPagination: {
        orderby: 'cast(ven_numero as float) desc',
    },
    fieldMap: new Map([['ven_numero', 'cast(ven_numero as float)']]),
    filter: {
        data_fim: dayjs().format(DATE_FORMAT),
        data_inicio: dayjs().subtract(1, 'month').format(DATE_FORMAT),
        data_tipo: VendaDataTipo.DataGeracao,
    },
});

export const useVendaPagination = () =>
    useVendaPaginationStore(
        useShallow((s) => ({
            apiPagination: s.apiPagination,
            filter: s.filter,
            onTableChange: s.onTableChange,
            setFilter: s.setFilter,
            tablePagination: s.tablePagination,
            updateTotalAndPagination: s.updateTotalAndPagination,
        })),
    );

export const resetVendaPaginationOrderBy = () => {
    useVendaPaginationStore.setState((state) => ({
        apiPagination: {
            ...state.apiPagination,
            orderby: 'cast(ven_numero as float) desc',
        },
    }));
};
